"use client";

/**
 * 顧客ランクの集計枠。
 * ★Sランク、成約率、件数、区分を1枠に出す。
 *
 * 2026-10-21 TanabeDaigo
 * 顧客評価一覧に直書きしていた枠を、他画面でも使える部品にした。
 */
import styled from "styled-components";

export const CORP_EVAL_RANK_META = [
  { code: "S", color: "#f59e0b", ink: "#ffffff", border: "#fde68a", bg: "#fffbeb", note: "最優先顧客" },
  { code: "A", color: "#2563eb", ink: "#ffffff", border: "#bfdbfe", bg: "#eff6ff", note: "安定高成約顧客" },
  { code: "B", color: "#059669", ink: "#ffffff", border: "#a7f3d0", bg: "#ecfdf5", note: "通常取引顧客" },
  { code: "C", color: "#6b7280", ink: "#ffffff", border: "#e5e7eb", bg: "#f8fafc", note: "改善・フォロー対象顧客" },
];

/**
 * ランクコードの表示名。
 * @param {string} code
 * @returns {string}
 */
export function corpEvalRankLabel(code) {
  if (code === "S") return "★Sランク";
  if (code === "A" || code === "B" || code === "C") return `${code}ランク`;
  return "";
}

/**
 * @param {string} code
 * @returns {Object|null}
 */
function rankMeta(code) {
  return CORP_EVAL_RANK_META.find((item) => item.code === code) || null;
}

const Badge = styled.span`
  display: inline-block;
  min-width: 1.5rem;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: ${(p) => p.$color};
  color: ${(p) => p.$ink || "#fff"};
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
`;

const Card = styled.div`
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  border-radius: 12px;
  padding: 0.85rem 1rem;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Foot = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.65rem;
`;

const Count = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
`;

const Meta = styled.div`
  color: #64748b;
  font-size: 0.75rem;
`;

/**
 * ランクの色付き表示。
 * @param {{ rank: string }} props
 * @returns {JSX.Element|null}
 */
export function RankBadge({ rank }) {
  const meta = rankMeta(rank);
  if (!meta) return null;
  return (
    <Badge $color={meta.color} $ink={meta.ink}>
      {corpEvalRankLabel(rank)}
    </Badge>
  );
}

/**
 * 成約率の表示文字。件数 0 のときは出さない。
 * @param {number|string} rate
 * @param {number} count
 * @returns {string}
 */
function formatCardRate(rate, count) {
  if (!count) return "—";
  const number = Number(rate);
  if (!Number.isFinite(number)) return "—";
  return `${number.toFixed(1)}%`;
}

/**
 * ランク集計の1枠。
 * @param {{ rank: string, rate?: number|string, count?: number|string }} props
 * @returns {JSX.Element|null}
 */
export function RankSummaryCard({ rank, rate, count }) {
  const meta = rankMeta(rank);
  if (!meta) return null;
  const total = Number(count) || 0;
  return (
    <Card $border={meta.border} $bg={meta.bg}>
      <Head>
        <RankBadge rank={rank} />
        <Meta>成約率 {formatCardRate(rate, total)}</Meta>
      </Head>
      <Foot>
        <Count>{total} 件</Count>
        <Meta>{meta.note}</Meta>
      </Foot>
    </Card>
  );
}
